import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { fadeInUp } from '../constants/animations';
import { buildApiUrl } from '../utils/api';
import './VendorApply.css';

// Event-agnostic vendor registration. Event pages link here; this page never
// names a venue, date, or price. Those live on the event page and in outreach.
const CONTACT = {
  phone: '(904) 750-4885',
  phoneHref: 'tel:19047504885'
};

const VENDOR_TYPES = [
  { value: 'retail', label: 'Retail / boutique' },
  { value: 'handmade', label: 'Handmade / art' },
  { value: 'food', label: 'Food & drink' },
  { value: 'services', label: 'Services' },
  { value: 'other', label: 'Other' }
];

const WEBSITE_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'instagram', label: 'Instagram only' }
];

const PAYMENT_OPTIONS = [
  { value: 'square', label: 'Square' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'cash', label: 'Cash only' },
  { value: 'other', label: 'Other' }
];

const MAPS_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Not sure' }
];

const INITIAL_FORM = {
  businessName: '',
  contactName: '',
  phone: '',
  email: '',
  instagram: '',
  vendorType: '',
  products: '',
  hasWebsite: '',
  websiteUrl: '',
  paymentMethod: '',
  onGoogleMaps: '',
  notes: '',
  consent: false
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const HONEYPOT_STYLE = { position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0, pointerEvents: 'none' };

function Required() {
  return <span aria-hidden="true">*</span>;
}

function ChoiceGroup({ name, legend, options, value, onChange }) {
  return (
    <fieldset className="va-choice">
      <legend>{legend} <Required /></legend>
      <div className="va-choice-options">
        {options.map((option) => (
          <label key={option.value} className={`va-chip${value === option.value ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              required
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function SuccessCard({ email }) {
  return (
    <motion.section
      className="va-card va-success"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      aria-live="polite"
    >
      <CheckCircle size={44} className="va-success-icon" aria-hidden="true" />
      <h1>You&apos;re registered.</h1>
      <p>
        We&apos;ll reach out within 48 hours by text or email, and you get first look when a booth opens at an
        upcoming event. Keep an eye on <strong>{email}</strong>.
      </p>
      <p className="va-muted">
        Questions? Text <a href={CONTACT.phoneHref}>{CONTACT.phone}</a>.
      </p>
    </motion.section>
  );
}

function VendorApply() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const choose = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    if (!form.businessName.trim()) return 'Business name is required.';
    if (!form.contactName.trim()) return 'Your name is required.';
    if (form.phone.replace(/\D/g, '').length < 10) return 'Enter a valid phone number.';
    if (!EMAIL_RE.test(form.email.trim())) return 'Enter a valid email address.';
    if (!form.vendorType) return 'Pick the closest vendor type.';
    if (!form.products.trim()) return 'Tell us what you sell.';
    if (!form.hasWebsite || !form.paymentMethod || !form.onGoogleMaps) return 'Please answer the three quick questions.';
    if (!form.consent) return 'Please agree to be contacted so we can reach you about booths.';
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    const honeypot = formRef.current ? formRef.current.querySelector('input[data-honeypot="vendor"]') : null;
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(buildApiUrl('/api/vendor-applications'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, website: honeypot ? honeypot.value : '' })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Submission failed. Please try again.');
      setSubmitted(true);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="va-page">
        <div className="va-container">
          <SuccessCard email={form.email.trim()} />
        </div>
      </main>
    );
  }

  return (
    <main className="va-page">
      <div className="va-container">
        <motion.header className="va-hero" initial="hidden" animate="visible">
          <motion.span className="va-kicker" variants={fadeInUp} custom={0}>Versa Launch Events</motion.span>
          <motion.h1 variants={fadeInUp} custom={1}>
            Register as a <span className="va-gold">vendor</span>.
          </motion.h1>
          <motion.p variants={fadeInUp} custom={2}>
            We host markets, pop-ups, and day-into-night parties around Jacksonville. Register once. When a booth
            opens, you&apos;re the first to hear, and every vendor gets a free listing on the event page.
          </motion.p>
        </motion.header>

        <motion.section
          className="va-card"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25, ease: 'easeOut' }}
        >
          <div className="va-card-head">
            <h2>Vendor registration</h2>
            <p>About two minutes. We reach out within 48 hours.</p>
          </div>

          <form ref={formRef} className="va-form" onSubmit={handleSubmit} noValidate>
            <p className="va-section-label">Your business</p>

            <div className="va-row">
              <div className="va-field">
                <label htmlFor="va-business">Business name <Required /></label>
                <input id="va-business" type="text" autoComplete="organization" value={form.businessName} onChange={update('businessName')} required />
              </div>
              <div className="va-field">
                <label htmlFor="va-name">Your name <Required /></label>
                <input id="va-name" type="text" autoComplete="name" value={form.contactName} onChange={update('contactName')} required />
              </div>
            </div>

            <div className="va-row">
              <div className="va-field">
                <label htmlFor="va-phone">Phone <Required /></label>
                <input id="va-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(904) 555-0100" value={form.phone} onChange={update('phone')} required />
              </div>
              <div className="va-field">
                <label htmlFor="va-email">Email <Required /></label>
                <input id="va-email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={update('email')} required />
              </div>
            </div>

            <div className="va-field">
              <label htmlFor="va-instagram">Instagram</label>
              <div className="va-prefixed">
                <span aria-hidden="true">@</span>
                <input id="va-instagram" type="text" autoComplete="off" autoCapitalize="none" placeholder="yourshop" value={form.instagram} onChange={update('instagram')} />
              </div>
            </div>

            <div className="va-field">
              <label htmlFor="va-type">Vendor type <Required /></label>
              <select id="va-type" value={form.vendorType} onChange={update('vendorType')} required>
                <option value="">Select...</option>
                {VENDOR_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="va-field">
              <label htmlFor="va-products">What do you sell? <Required /></label>
              <input id="va-products" type="text" placeholder="Vintage clothing, candles, tacos..." maxLength={300} value={form.products} onChange={update('products')} required />
              <span className="va-hint">This becomes your free vendor listing when you&apos;re booked.</span>
            </div>

            <p className="va-section-label">Three quick questions</p>

            <ChoiceGroup name="hasWebsite" legend="Do you have a website?" options={WEBSITE_OPTIONS} value={form.hasWebsite} onChange={choose('hasWebsite')} />
            {form.hasWebsite === 'yes' && (
              <div className="va-field">
                <label htmlFor="va-website">Website address</label>
                <input id="va-website" type="url" inputMode="url" autoComplete="url" placeholder="yourshop.com" value={form.websiteUrl} onChange={update('websiteUrl')} />
              </div>
            )}
            <ChoiceGroup name="paymentMethod" legend="How do you take payment?" options={PAYMENT_OPTIONS} value={form.paymentMethod} onChange={choose('paymentMethod')} />
            <ChoiceGroup name="onGoogleMaps" legend="Is your business on Google Maps?" options={MAPS_OPTIONS} value={form.onGoogleMaps} onChange={choose('onGoogleMaps')} />

            <div className="va-field">
              <label htmlFor="va-notes">Anything we should know?</label>
              <textarea id="va-notes" rows={3} placeholder="Power needs, tent size, food license, questions..." maxLength={1500} value={form.notes} onChange={update('notes')} />
            </div>

            <label className="va-consent">
              <input type="checkbox" checked={form.consent} onChange={(event) => setForm((prev) => ({ ...prev, consent: event.target.checked }))} required />
              <span>
                I agree that Versa Launch may contact me about vendor opportunities and events by email, phone, and text
                message at the number provided. Message and data rates may apply. Reply STOP to opt out.
                See our <Link to="/privacy-policy">Privacy Policy</Link> and <Link to="/terms-of-service">Terms</Link>. <Required />
              </span>
            </label>

            <input
              type="text"
              name="website"
              data-honeypot="vendor"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              defaultValue=""
              style={HONEYPOT_STYLE}
            />

            {error && (
              <div className="va-error" role="alert">
                <AlertCircle size={16} aria-hidden="true" /> {error}
              </div>
            )}

            <button type="submit" className="va-btn" disabled={submitting}>
              {submitting ? 'Sending...' : 'Register as a vendor'}
            </button>

            <p className="va-fine">
              Vendors handle their own permits and sales tax at every event. Food vendors need a current Florida
              food-service or mobile food license. Questions? Text <a href={CONTACT.phoneHref}>{CONTACT.phone}</a>.
            </p>
          </form>
        </motion.section>
      </div>
    </main>
  );
}

export default VendorApply;
